<template>
	<div class="w-full pt-8">
		<div class="bg-white border-gray-200 flex w-full flex-col items-start rounded-lg border lg:flex-row">
			<div class="flex flex-shrink-0 items-center justify-center self-stretch pe-8 ps-8 lg:self-start">
				<div class="text-yellow-500 -mt-8 mb-2 flex h-24 w-24 items-center justify-center rounded-lg lg:mb-8">
					<Icon name="mdi:history" class="h-24 w-24" />
				</div>
			</div>
			<div class="bg-re flex w-full flex-grow flex-col pb-6 ps-6 pt-6 lg:pb-4 lg:ps-0 lg:pt-4">
				<div class="flex items-center">
					<span class="text-neutral-900 relative font-bold duration-300">
						<span
							class="bg-yellow-500 absolute bottom-0 start-0 -ms-3 mb-1 h-2 w-10 rounded-full opacity-20"
						/>
						<span class="absolute left-0 top-0 line-clamp-1 block">
							{{ (data && data.message) || "~" }}
						</span>
						<span class="z-1 line-clamp-1 block opacity-0">
							{{ (data && data.message) || "~" }}
						</span>
					</span>
					<div class="flex-grow" />
				</div>
				<div class="mt-4 grid grid-cols-2 gap-3 self-stretch">
					<div class="flex items-center text-sm">
						<Icon name="mdi:account" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("responsible_name") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ ((data && data.user_firstname) || "~") + " " + ((data && data.user_lastname) || "~") }}
						</span>
					</div>
					<div v-if="data && data.user_email" class="flex items-center text-sm">
						<Icon name="mdi:account" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("responsible_email") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ (data && data.user_email) || "~" }}
						</span>
					</div>
					<div v-if="data && data.user_mobile" class="flex items-center text-sm">
						<Icon name="mdi:phone" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("responsible_mobile") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ (data && data.user_mobile) || "~" }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:text" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("text") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span
							class="cursor-pointer font-medium text-primary"
							@click="openShowTextDialog($t('text'), data?.message)"
						>
							{{ $t("show") }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:text" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("values") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span
							class="cursor-pointer font-medium text-primary"
							@click="openShowCodeDialog($t('values'), data && data.value)"
						>
							{{ $t("show") }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:clock-time-nine" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("type") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ (data && data.type) || "~" }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:clock-time-nine" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("creation_date") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ getDateString(data && data.created) || "~" }}
						</span>
					</div>
				</div>
			</div>
		</div>
		<BaseModal
			:visible="state.isTextShowDialogVisible"
			:title="state.textShowDialogData.title"
			size="xl"
			hide-footer
			@close="state.isTextShowDialogVisible = false"
		>
			<div class="flex flex-col">
				<div v-if="state.textShowDialogData.text !== null" class="text-neutral-700 p-6 text-sm">
					{{ state.textShowDialogData.text || "~" }}
				</div>
				<pre
					v-if="state.textShowDialogData.code !== null"
					class="text-neutral-700 overflow-x-auto p-6 text-sm"
					dir="ltr"
					>{{ formattedCode || "~" }}</pre
				>
			</div>
		</BaseModal>
	</div>
</template>

<script setup lang="ts">
import type { Log } from "~/models/log"

const moment = useMoment()

defineProps<{
	data: Log | null
}>()

const state = reactive({
	isTextShowDialogVisible: false,
	textShowDialogData: {
		title: "",
		text: "" as null | string,
		code: "" as null | string
	}
})

const formattedCode = computed(() => {
	const code = state.textShowDialogData.code || ""
	try {
		return JSON.stringify(JSON.parse(code), null, "\t")
	} catch (e) {
		return code
	}
})

const getDateString = (d: any) => {
	if (!d) return "~"
	return moment.utc(d, "YYYY/MM/DD").local().format("YYYY/MM/DD")
}

const openShowTextDialog = (title: string, text?: string) => {
	state.textShowDialogData = {
		title,
		text: text || "~",
		code: null
	}
	state.isTextShowDialogVisible = true
}

const openShowCodeDialog = (title: string, code: any) => {
	state.textShowDialogData = {
		title,
		text: null,
		code: code || "~"
	}
	state.isTextShowDialogVisible = true
}
</script>
